import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';

import { paths } from '@/routes/paths';

interface TabEntry {
  id: 'account' | 'preferences' | 'notifications' | 'privacy';
  to: string;
  /** SVG path data for the leading icon (path d attribute). */
  iconPath: string;
}

const TABS: readonly TabEntry[] = [
  {
    id: 'account',
    to: paths.settings.account,
    iconPath: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2 M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  },
  {
    id: 'preferences',
    to: paths.settings.preferences,
    iconPath: 'M12 1v6m0 6v8 M5 8h14 M5 16h14',
  },
  {
    id: 'notifications',
    to: paths.settings.notifications,
    iconPath: 'M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9 M13.73 21a2 2 0 0 1-3.46 0',
  },
  {
    id: 'privacy',
    to: paths.settings.privacy,
    iconPath: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  },
] as const;

export function SettingsSidebar() {
  const { t } = useTranslation('settings');

  return (
    <Box component="nav" aria-label={t('navTitle')} sx={{ position: 'sticky', top: 96 }}>
      <Typography
        sx={{
          fontFamily: '"Cairo Variable", sans-serif',
          fontSize: 12,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'text.disabled',
          fontWeight: 600,
          mb: 1.5,
          paddingInlineStart: 1.5,
        }}
      >
        {t('navTitle')}
      </Typography>
      <Stack spacing={0.25}>
        {TABS.map((tab) => (
          <Stack
            key={tab.id}
            component={NavLink}
            to={tab.to}
            direction="row"
            alignItems="center"
            spacing={1.25}
            sx={(theme) => {
              const activeText =
                theme.palette.mode === 'dark'
                  ? theme.palette.primary.light
                  : theme.palette.primary.dark;
              return {
                textDecoration: 'none',
                color: theme.palette.text.secondary,
                fontSize: 13.5,
                fontWeight: 500,
                borderRadius: (t) => `${t.shape.borderRadius}px`,
                paddingInlineStart: 1.5,
                paddingInlineEnd: 1.5,
                py: 1,
                transitionProperty: 'background-color, color',
                transitionDuration: '0.15s',
                '& svg': { color: theme.palette.text.disabled },
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                  color: theme.palette.text.primary,
                },
                '&.active': {
                  backgroundColor: `${theme.palette.primary.main}14`,
                  color: activeText,
                  fontWeight: 600,
                  '& svg': { color: theme.palette.primary.main },
                },
              };
            }}
          >
            <Box
              component="svg"
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.8}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
              sx={{ flexShrink: 0 }}
            >
              <path d={tab.iconPath} />
            </Box>
            <span>{t(`tabs.${tab.id}`)}</span>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}
